
import React from 'react';
import type { Sport, NavItem, UserStory } from './types';

// Icons
import HomeIcon from './components/icons/HomeIcon';
import SearchIcon from './components/icons/SearchIcon';
import MapPinIcon from './components/icons/MapPinIcon';
import MessageIcon from './components/icons/MessageIcon';
import UserIcon from './components/icons/UserIcon';
import ScooterIcon from './components/icons/ScooterIcon';
import BellIcon from './components/icons/BellIcon';
import ZIcon from './components/icons/ZIcon';
import LightningIcon from './components/icons/LightningIcon';
import SwapIcon from './components/icons/SwapIcon';

// Bump this whenever a sport icon PNG in /public is replaced. Firebase
// Hosting serves those with Cache-Control: max-age=3600, so without a
// version bump a browser that already cached the old file won't pick up
// the new one until that hour expires - same pattern as APP_LOGO_SRC below.
export const SPORT_ICON_VERSION = '20260928';

// Sport Icons
const SkateboardIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M6.33 11.333h11.34c.583.004 1.144-.22 1.56-.62.416-.4.654-.95.63-1.52-.024-.57-.272-1.107-.69-1.507-.418-.4-1.002-.633-1.61-.613H6.33c-.608-.02-1.192.213-1.61.613-.418.4-.666.937-.69 1.507-.024-.57.214 1.12.63 1.52.416.4 1 .624 1.58.62h-.01Zm1.08 2.02h9.18c.504.003 1.01-.16 1.4-.46.39-.3.65-.75.74-1.22h-13.4c.09.47.35.88.74 1.22.39.3 1 .463 1.44.46Zm-3.23 3.99c.35.35.8.54 1.28.53h11.1c.48.01.93-.18 1.28-.53.35-.35.54-.8.53-1.28v-2.12H3.18v2.12c-.01.48.18.93.53 1.28ZM21.9 4H2.1C.94 4 0 4.94 0 6.1v11.8C0 19.06.94 20 2.1 20h19.8c1.16 0 2.1-.94 2.1-2.1V6.1C24 4.94 23.06 4 21.9 4Z"/></svg>
);
const BmxIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M14.93 11.23c-1.37.16-2.5-1.14-2.34-2.52.14-1.21 1.22-2.11 2.4-2.11 1.38 0 2.5 1.12 2.5 2.5 0 1.26-1.02 2.27-2.56 2.13Zm-7.38-2.67c-1.38 0-2.5-1.12-2.5-2.5S6.17 3.56 7.55 3.56c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5Zm12.45 2.44L17.7 20.4c-.45.92-1.42 1.5-2.5 1.5h-6c-1.21 0-2.3-.77-2.78-1.9L2 12h5.5l.43 2.56L12 11.4l-1.95-3.03 2.1-.87 3.32 5.16.88-2.16L12.5 4.5h5l2.5 5.94Z"/></svg>
);
const LongboardIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M19.98 6.54c0-1.2-1.03-2.18-2.31-2.02l-11.34.82c-1.1.1-1.92 1.13-1.78 2.24l.11.82h15.2l.12-.86Zm-15.65 3.92.1.84c.16 1.1.98 2.13 2.08 2.24l11.34-.82c1.27-.16 2.31-1.14 2.31-2.26v-.86H4.33Z"/></svg>
);
const SkiingIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M21.6 15.68c-.64.55-1.4.92-2.22 1.05l-4.5.7c-.55.08-1.08.3-1.52.62-1.2.9-2.88.9-4.08 0-.44-.32-.97-.54-1.52-.62l-4.5-.7c-.82-.13-1.58-.5-2.22-1.05C.4 15.2.02 14.3.17 13.4c.15-.9.8-1.65 1.68-1.9l19.9-3.1c.88-.15 1.7.35 1.95 1.23.25.88-.15 1.83-.9 2.25l-.2.1Z"/><path d="M4.6 6.38c.64-.55 1.4-.92 2.22-1.05l4.5-.7c.55-.08 1.08-.3 1.52-.62 1.2-.9 2.88.9 4.08 0 .44.32.97.54 1.52.62l4.5.7c.82.13 1.58.5 2.22 1.05.64.48 1.02 1.38.87 2.28-.15.9-.8 1.65-1.68 1.9l-19.9 3.1c-.88.15-1.7-.35-1.95-1.23-.25-.88.15-1.83.9-2.25l.2-.1Z"/></svg>
);
const SnowboardingIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M20.2 2H6.91c-1.62 0-3.1.93-3.83 2.43L.41 9.87c-.8 1.6.08 3.53 1.7 4.3l9.46 4.73c1.6.8 3.53-.1 4.3-1.7l2.67-5.45c.18-.36.27-.75.27-1.15V5c0-1.66-1.34-3-3-3Zm-2.83 7.5-.94.46-2-1-2 1-.94-.46-2.07 4.23.95.47 2-1 2 1 .95-.47 2.07-4.23Z"/></svg>
);
const RollerbladingIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <img
    src="https://img.icons8.com/fluency/96/roller-skates.png"
    alt="Rollerblade"
    style={{
      width: (props.width as string) || (props.className?.includes('h-') ? undefined : '24px'),
      height: (props.height as string) || (props.className?.includes('h-') ? undefined : '24px'),
      objectFit: 'contain'
    }}
    className={props.className}
  />
);
const DjMtbIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <img
    src={`/DJ.png?v=${SPORT_ICON_VERSION}`}
    alt="DJ/MTB"
    style={{
      width: (props.width as string) || (props.className?.includes('h-') ? undefined : '24px'),
      height: (props.height as string) || (props.className?.includes('h-') ? undefined : '24px'),
      objectFit: 'contain'
    }}
    className={props.className}
  />
);
const SurfingIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <img
    src={`/surfing.png?v=${SPORT_ICON_VERSION}`}
    alt="Surf"
    style={{
      width: (props.width as string) || (props.className?.includes('h-') ? undefined : '24px'),
      height: (props.height as string) || (props.className?.includes('h-') ? undefined : '24px'),
      objectFit: 'contain'
    }}
    className={props.className}
  />
);
const ParkourIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <img
    src={`/parkour.png?v=${SPORT_ICON_VERSION}`}
    alt="Parkour"
    style={{
      width: (props.width as string) || (props.className?.includes('h-') ? undefined : '24px'),
      height: (props.height as string) || (props.className?.includes('h-') ? undefined : '24px'),
      objectFit: 'contain'
    }}
    className={props.className}
  />
);
const DirtbikingIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <img
    src={`/dirtbike.png?v=${SPORT_ICON_VERSION}`}
    alt="Dirtbike"
    style={{
      width: (props.width as string) || (props.className?.includes('h-') ? undefined : '24px'),
      height: (props.height as string) || (props.className?.includes('h-') ? undefined : '24px'),
      objectFit: 'contain'
    }}
    className={props.className}
  />
);
const SkydivingIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <img
    src={`/skydiving.png?v=${SPORT_ICON_VERSION}`}
    alt="Skydive"
    style={{
      width: (props.width as string) || (props.className?.includes('h-') ? undefined : '24px'),
      height: (props.height as string) || (props.className?.includes('h-') ? undefined : '24px'),
      objectFit: 'contain'
    }}
    className={props.className}
  />
);
const WingsuitIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <img
    src={`/wingsuit.png?v=${SPORT_ICON_VERSION}`}
    alt="Wingsuit"
    style={{
      width: (props.width as string) || (props.className?.includes('h-') ? undefined : '24px'),
      height: (props.height as string) || (props.className?.includes('h-') ? undefined : '24px'),
      objectFit: 'contain'
    }}
    className={props.className}
  />
);
const ParaglidingIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <img
    src={`/paragliding.png?v=${SPORT_ICON_VERSION}`}
    alt="Paraglide"
    style={{
      width: (props.width as string) || (props.className?.includes('h-') ? undefined : '24px'),
      height: (props.height as string) || (props.className?.includes('h-') ? undefined : '24px'),
      objectFit: 'contain'
    }}
    className={props.className}
  />
);
const RaftingIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <img
    src={`/rafting.png?v=${SPORT_ICON_VERSION}`}
    alt="Raft"
    style={{
      width: (props.width as string) || (props.className?.includes('h-') ? undefined : '24px'),
      height: (props.height as string) || (props.className?.includes('h-') ? undefined : '24px'),
      objectFit: 'contain'
    }}
    className={props.className}
  />
);
const ClimbingIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <img
    src={`/climbing.png?v=${SPORT_ICON_VERSION}`}
    alt="Climb"
    style={{
      width: (props.width as string) || (props.className?.includes('h-') ? undefined : '24px'),
      height: (props.height as string) || (props.className?.includes('h-') ? undefined : '24px'),
      objectFit: 'contain'
    }}
    className={props.className}
  />
);

// Display names are the plain sport ("Skateboard", "Ski"...). The ids stay as they are: they're what's stored in the
// database (posts, spots, shops, profiles, listings) and what the icon files are keyed on.
export const SPORTS: Sport[] = [
  { id: 'skateboarding', name: 'Skateboard', icon: SkateboardIcon },
  { id: 'bmx', name: 'BMX', icon: BmxIcon },
  { id: 'dj-mtb', name: 'DJ/MTB', icon: DjMtbIcon },
  { id: 'longboarding', name: 'Longboard', icon: LongboardIcon },
  { id: 'skiing', name: 'Ski', icon: SkiingIcon },
  { id: 'snowboarding', name: 'Snowboard', icon: SnowboardingIcon },
  { id: 'rollerblading', name: 'Rollerblade', icon: RollerbladingIcon },
  { id: 'scootering', name: 'Scooter', icon: ScooterIcon },
  { id: 'surfing', name: 'Surf', icon: SurfingIcon },
  { id: 'parkour', name: 'Parkour', icon: ParkourIcon },
  { id: 'climbing', name: 'Climb', icon: ClimbingIcon },
  { id: 'dirtbiking', name: 'Dirtbike', icon: DirtbikingIcon },
  { id: 'skydiving', name: 'Skydive', icon: SkydivingIcon },
  { id: 'wingsuit', name: 'Wingsuit', icon: WingsuitIcon },
  { id: 'paragliding', name: 'Paraglide', icon: ParaglidingIcon },
  { id: 'rafting', name: 'Raft', icon: RaftingIcon },
];

export const SPOT_FEATURES: { id: import('./types').SpotFeatureId; label: string; emoji: string }[] = [
  { id: 'rail', label: 'Rail', emoji: '〰️' },
  { id: 'stairs', label: 'Stairs', emoji: '🪜' },
  { id: 'gap', label: 'Gap', emoji: '↔️' },
  { id: 'bowl', label: 'Bowl', emoji: '🥣' },
];

export const NAV_ITEMS: NavItem[] = [
    { id: 'feed', label: 'Feed', icon: HomeIcon },
    { id: 'zips', label: 'Zips', icon: LightningIcon },
    { id: 'discover', label: 'Discover', icon: SearchIcon },
    { id: 'spot-map', label: 'Spots', icon: MapPinIcon },
    { id: 'marketplace', label: 'Buy & Sell', icon: SwapIcon },
    { id: 'messenger', label: 'Messages', icon: MessageIcon },
    { id: 'profile', label: 'Profile', icon: UserIcon },
];

export const NOTIFICATIONS_NAV: NavItem = { id: 'notifications', label: 'Notifications', icon: BellIcon };
export const APP_LOGO_SRC = '/logo.png?v=20260331b';
// Keep in sync with the withinTextLimit(..., 'bio', 400) check in firestore.rules.
export const BIO_MAX_LENGTH = 400;

// Stories are kept client-side for the demo as they are ephemeral.
// In a production app, these would be fetched from a dedicated media service.
export const STORIES: UserStory[] = [
  // This can be populated with initial mock data if desired,
  // but will be primarily driven by user actions in the app.
];

export const LISTING_CONDITIONS: { id: import('./types').ListingCondition; label: string }[] = [
  { id: 'new', label: 'New' },
  { id: 'like-new', label: 'Like new' },
  { id: 'good', label: 'Good' },
  { id: 'fair', label: 'Fair' },
];

// Marketplace categories: every sport plus a catch-all.
export const LISTING_CATEGORIES: { id: string; label: string }[] = [
  ...SPORTS.map((sport) => ({ id: sport.id, label: sport.name })),
  { id: 'other', label: 'Other' },
];
